import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip if on OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setAdmin(response.data.admin);
    } catch (error) {
      setUser(null);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const login = async (email, password) => {
    // First login as regular user
    await axios.post(`${API}/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    // Then check admin access
    await checkAuth();
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setAdmin(null);
  };

  const refreshAuth = async () => {
    await checkAuth();
  };

  // Check role permissions
  const hasRole = (allowedRoles) => {
    if (!admin) return false;
    return allowedRoles.includes(admin.role);
  };

  const isSuperAdmin = () => hasRole(['super_admin']);
  const canManageMerchants = () => hasRole(['super_admin']);
  const canViewMerchants = () => hasRole(['super_admin', 'support_agent', 'reviewer']);
  const canReviewTransactions = () => hasRole(['super_admin', 'reviewer']);
  const canAddNotes = () => hasRole(['super_admin', 'support_agent']);

  return (
    <AdminAuthContext.Provider value={{
      user,
      admin,
      loading,
      login,
      loginWithGoogle,
      logout,
      refreshAuth,
      isAuthenticated: !!user && !!admin,
      hasRole,
      isSuperAdmin,
      canManageMerchants,
      canViewMerchants,
      canReviewTransactions,
      canAddNotes
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthProvider;
